import { useEffect, useRef, useState } from 'react'

interface WidgetConfig {
  clientId: string
  widgetKey?: string
  position: string
  theme: string
  primaryColor: string
  autoOpen: boolean
  showOnMobile: boolean
  proactiveEnabled?: boolean
  proactiveDelay?: number
  proactiveMessage?: string
}

interface WidgetPreviewProps {
  config: WidgetConfig
  organizationName?: string
  websiteUrl?: string
}

export default function WidgetPreview({ config, organizationName }: WidgetPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [key, setKey] = useState(0)

  // In production (app.legallady.ai, railway, etc.) the dashboard and API are
  // served from the same origin, so window.location.origin is the correct
  // default. Only fall back to localhost for a local dev dashboard.
  const apiUrl = import.meta.env.VITE_API_URL ||
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3000'
      : window.location.origin)

  // Re-mount iframe when key config values change. Previously this also
  // consumed a thum.io screenshot of the customer's actual website as a
  // background, but thum.io's free tier started returning "Image not
  // authorized" placeholders — the mock-law-firm layout below works as a
  // cleaner, vendor-free backdrop anyway.
  useEffect(() => {
    setKey(prev => prev + 1)
    setIsLoaded(false)
    setError(null)
  }, [config.position, config.theme, config.primaryColor, config.clientId])

  useEffect(() => {
    if (!iframeRef.current) return
    const iframe = iframeRef.current

    const proactiveDelay = config.proactiveEnabled ? (config.proactiveDelay || 15) : 0
    const proactiveMessage = config.proactiveMessage
      ? JSON.stringify(config.proactiveMessage).slice(1, -1)  // Remove surrounding quotes
      : ''

    const iframeContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Widget Preview</title>
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Georgia', 'Times New Roman', serif;
      background: #f9f8f6;
      color: #1a1a1a;
      min-height: 100vh;
    }
    @media (max-width: 640px) {
      .mock-nav { padding: 0 16px; }
      .mock-nav-links { display: none; }
      .mock-hero { padding: 32px 16px 24px; }
      .mock-hero h1 { font-size: 24px; }
      .mock-areas { grid-template-columns: 1fr; padding: 0 16px 32px; }
      .mock-trust { flex-wrap: wrap; gap: 12px; padding: 16px; }
    }

    /* Mock law firm nav */
    .mock-nav {
      background: #fff;
      border-bottom: 1px solid #e5e7eb;
      padding: 0 32px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 64px;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .mock-logo {
      font-size: 18px;
      font-weight: 700;
      color: #111;
      letter-spacing: -0.3px;
    }
    .mock-logo span {
      color: #1e40af;
    }
    .mock-nav-links {
      display: flex;
      gap: 28px;
      list-style: none;
    }
    .mock-nav-links a {
      font-family: system-ui, sans-serif;
      font-size: 14px;
      color: #374151;
      text-decoration: none;
      font-weight: 500;
    }
    .mock-cta-btn {
      font-family: system-ui, sans-serif;
      font-size: 13px;
      font-weight: 600;
      padding: 8px 20px;
      border-radius: 6px;
      color: #fff;
      border: none;
      cursor: default;
      background: #1e40af;
    }

    /* Mock hero */
    .mock-hero {
      padding: 60px 32px 48px;
      max-width: 720px;
    }
    .mock-badge {
      display: inline-block;
      font-family: system-ui, sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #1e40af;
      background: #1e40af18;
      padding: 4px 12px;
      border-radius: 99px;
      margin-bottom: 20px;
    }
    .mock-hero h1 {
      font-size: 38px;
      font-weight: 700;
      line-height: 1.2;
      color: #111;
      margin-bottom: 16px;
    }
    .mock-hero h1 em {
      font-style: normal;
      color: #1e40af;
    }
    .mock-hero p {
      font-family: system-ui, sans-serif;
      font-size: 16px;
      line-height: 1.7;
      color: #4b5563;
      max-width: 560px;
    }
    .mock-hero-btns {
      display: flex;
      gap: 12px;
      margin-top: 28px;
    }
    .mock-btn-primary {
      font-family: system-ui, sans-serif;
      font-size: 14px;
      font-weight: 600;
      padding: 10px 24px;
      border-radius: 6px;
      color: #fff;
      background: #1e40af;
      border: none;
      cursor: default;
    }
    .mock-btn-secondary {
      font-family: system-ui, sans-serif;
      font-size: 14px;
      font-weight: 600;
      padding: 10px 24px;
      border-radius: 6px;
      color: #374151;
      background: #fff;
      border: 1px solid #d1d5db;
      cursor: default;
    }

    /* Mock practice areas */
    .mock-areas {
      padding: 0 32px 48px;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      max-width: 720px;
    }
    .mock-card {
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 20px;
    }
    .mock-card-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #1e40af;
      margin-bottom: 10px;
    }
    .mock-card h3 {
      font-family: system-ui, sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: #111;
      margin-bottom: 6px;
    }
    .mock-card p {
      font-family: system-ui, sans-serif;
      font-size: 12px;
      color: #6b7280;
      line-height: 1.5;
    }

    /* Trust bar */
    .mock-trust {
      padding: 20px 32px;
      border-top: 1px solid #e5e7eb;
      background: #fff;
      display: flex;
      gap: 32px;
      align-items: center;
    }
    .mock-trust-item {
      font-family: system-ui, sans-serif;
      font-size: 13px;
      color: #374151;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .mock-trust-item strong {
      font-weight: 700;
      color: #1e40af;
    }
  </style>
</head>
<body>

  <!-- Mock Law Firm Website -->
  <nav class="mock-nav">
    <div class="mock-logo">${organizationName ? organizationName.replace(/&/g, '&amp;').replace(/</g, '&lt;') : 'Your Law Firm'}</div>
    <ul class="mock-nav-links">
      <li><a href="#">Practice Areas</a></li>
      <li><a href="#">About</a></li>
      <li><a href="#">Results</a></li>
      <li><a href="#">Contact</a></li>
    </ul>
    <button class="mock-cta-btn">Free Consultation</button>
  </nav>

  <section class="mock-hero">
    <div class="mock-badge">Trusted Legal Representation</div>
    <h1>Fighting for Your Rights<br>When It Matters <em>Most</em></h1>
    <p>
      With over 20 years of experience, our attorneys have recovered millions
      for clients across personal injury, family law, and criminal defense cases.
      Your first consultation is always free.
    </p>
    <div class="mock-hero-btns">
      <button class="mock-btn-primary">Free Case Review</button>
      <button class="mock-btn-secondary">View Our Results</button>
    </div>
  </section>

  <section class="mock-areas">
    <div class="mock-card">
      <div class="mock-card-dot"></div>
      <h3>Personal Injury</h3>
      <p>Car accidents, slip &amp; fall, workplace injuries.</p>
    </div>
    <div class="mock-card">
      <div class="mock-card-dot"></div>
      <h3>Family Law</h3>
      <p>Divorce, custody, child support &amp; adoption.</p>
    </div>
    <div class="mock-card">
      <div class="mock-card-dot"></div>
      <h3>Criminal Defense</h3>
      <p>DUI, felony, misdemeanor &amp; appeals.</p>
    </div>
  </section>

  <div class="mock-trust">
    <div class="mock-trust-item">⭐ <strong>4.9/5</strong> Google Reviews</div>
    <div class="mock-trust-item">✓ <strong>500+</strong> Cases Won</div>
    <div class="mock-trust-item">📞 <strong>24/7</strong> Available</div>
    <div class="mock-trust-item">💰 No Win, <strong>No Fee</strong></div>
  </div>

  <!-- Real Widget -->
  <script>
    (function() {
      var config = {
        clientId: '${config.clientId}',
        ${config.widgetKey ? `widgetKey: '${config.widgetKey}',` : ''}
        apiUrl: '${apiUrl}',
        websocketUrl: 'wss://preview-disabled.invalid',
        position: '${config.position}',
        theme: '${config.theme}',
        branding: { primaryColor: '${config.primaryColor}' },
        autoOpen: true,
        showOnMobile: true,
        preview: true${proactiveDelay > 0 ? `,
        proactiveDelay: ${proactiveDelay}${proactiveMessage ? `,\n        proactiveMessage: '${proactiveMessage}'` : ''}` : ''}
      };

      var script = document.createElement('script');
      script.src = '${apiUrl}/widget/intake-chat.iife.js';
      script.onload = function() {
        try {
          var Chat = window.IntakeChat && window.IntakeChat.IntakeChat
            ? window.IntakeChat.IntakeChat
            : window.IntakeChat;
          if (typeof Chat === 'function') {
            new Chat(config);
            window.parent.postMessage({ type: 'widget-loaded' }, '*');
          } else {
            window.parent.postMessage({ type: 'widget-error', error: 'Widget class not found' }, '*');
          }
        } catch (e) {
          window.parent.postMessage({ type: 'widget-error', error: e.message }, '*');
        }
      };
      script.onerror = function() {
        window.parent.postMessage({ type: 'widget-error', error: 'Failed to load widget script from ' + '${apiUrl}' }, '*');
      };
      document.head.appendChild(script);
    })();
  </script>
</body>
</html>`

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
    if (iframeDoc) {
      iframeDoc.open()
      iframeDoc.write(iframeContent)
      iframeDoc.close()
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'widget-loaded') {
        setIsLoaded(true)
        setError(null)
      } else if (event.data.type === 'widget-error') {
        setError(event.data.error)
        setIsLoaded(true)
      }
    }

    window.addEventListener('message', handleMessage)

    const timeout = setTimeout(() => {
      if (!isLoaded) setIsLoaded(true)
    }, 6000)

    return () => {
      window.removeEventListener('message', handleMessage)
      clearTimeout(timeout)
    }
  }, [key])

  return (
    <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', height: '680px' }}>
      <iframe
        key={key}
        ref={iframeRef}
        title="Widget Preview"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />

      {!isLoaded && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(249,248,246,0.85)',
          backdropFilter: 'blur(4px)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '32px', height: '32px',
            border: '3px solid #e5e7eb',
            borderTopColor: config.primaryColor,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <p style={{ fontFamily: 'system-ui, sans-serif', fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
            Loading widget preview…
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {error && (
        <div style={{
          position: 'absolute', top: '16px', left: '16px', right: '16px',
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#991b1b', padding: '12px 16px', borderRadius: '8px',
          fontFamily: 'system-ui, sans-serif', fontSize: '13px'
        }}>
          <strong>Preview error:</strong> {error}
          <div style={{ marginTop: '4px', opacity: 0.75, fontSize: '12px' }}>
            Make sure the API is running and VITE_API_URL is set correctly.
          </div>
        </div>
      )}
    </div>
  )
}

declare global {
  interface Window { IntakeChat: any }
}
